import { useExternalScript } from 'src/common/hooks/use-external-script';

import type { ChiliPiperRouterNames } from '../constants';

enum ChiliPiperLanguageCodes {
  English = 'en_US',
  English_British = 'en_GB',
  German = 'de_DE',
  Danish = 'da_DK',
  Russian = 'ru_RU',
  Arabic = 'ar_AR',
  Italian = 'it_IT',
  French_Canadian = 'fr_CA',
  French = 'fr_FR',
  Spanish_Latin_America = 'es_LA',
  Spanish = 'es_ES',
  Portuguese_Brazilian = 'pt_BR',
  Portuguese = 'pt_PT',
  Japanese = 'ja_JP',
  Korean = 'ko_KR',
  Dutch = 'nl_NL',
  Norwegian = 'nb_NO',
  Chinese_Simplified = 'zh_CN',
  Chinese_Traditional = 'zh_TW',
  Chinese_Hong_Kong = 'zh_HK',
  Indonesian = 'id_ID',
  Thai = 'th_TH',
  Vietnamese = 'vi_VN',
  Polish = 'pl_PL',
  Romanian = 'ro_RO',
  Czech = 'cs_CZ',
  Hebrew = 'he_IL',
  Greek = 'el_GR',
  Turkish = 'tr_TR',
  Hungarian = 'hu_HU',
  Swedish = 'sv_SE',
}

// Stub type - SFDCUserId is not documented
type SFDCUserId = string;

// Source: https://help.chilipiper.com/hc/en-us/articles/360053800373-About-Concierge-Snippet-and-JS-API
interface ChiliPiperOptions {
  skipFormFallbackUponAccountDQ: boolean;
  formId: string;
  query: string;
  debug: boolean;
  map: boolean;
  domain: string;
  router: string;
  titleStyle: string;
  lead: object;
  onSuccess: () => void;
  onError: () => void;
  onClose: () => void;
  onRouted: () => void;
  domElement: string;
  closeOnOutside: boolean;
  dynamicRedirectLink: string;
  mobileRedirectLink: string;
  locale: ChiliPiperLanguageCodes;
  webHook: URL;
  disableRelation: boolean;
  skipOwnershipLogic: boolean;
  ownerId: SFDCUserId;
  event: object;
  accountId: string;
  type: string;
}

type ChiliPiperFunction = (
  subdomain: string,
  router: ChiliPiperRouterNames,
  options: Partial<ChiliPiperOptions>,
) => void;

type ChiliPiperFunctionNames = 'getQuery' | 'bookMeeting' | 'scheduling' | 'showCalendar' | 'submit';
type ChiliPiper = Record<ChiliPiperFunctionNames, ChiliPiperFunction>;

declare global {
  interface Window {
    ChiliPiper?: ChiliPiper;
  }
}

export const useChiliPiperJS = () => {
  const isLoading = useExternalScript('https://js.chilipiper.com/marketing.js', {
    onLoad() {
      if (!window.ChiliPiper) throw new Error('ChiliPiper script loaded but not available');
    },
  });

  if (isLoading) return { isLoading, chiliPiperScript: undefined };

  return { isLoading, chiliPiperScript: window.ChiliPiper };
};
